import React from 'react';
import PropTypes from 'prop-types';
import { ToggleLabel, CounterLabel, CheckboxLabel, Input, FieldBlock, SelectLabel, TextAreaLabel, CurrencyInputLabel } from "@weezevent/nacre";
import { Form, Label } from "semantic-ui-react";

export function LabelCounter({
	label,
	input: {
		value,
		onChange,
	},
	...rest
}){
	const handleChange = React.useCallback((ev, value) => {
		onChange(value);
	}, [onChange]);

	return (
		<CounterLabel
			debounceTimeout={ 700 }
			value={ parseInt(Array.isArray(value) ? value[0] : value) }
			label={ label }
			onChange={ handleChange }
			{ ...rest }
		/>
	);
}

LabelCounter.propTypes = {
	label: PropTypes.node,
	input: PropTypes.shape({
		value: PropTypes.any,
		onChange: PropTypes.func.isRequired,
	}),
};

export function CurrencyInput({
	label,
	input: {
		value,
		onChange,
	},
	...rest
}){
	const handleChange = React.useCallback(({value}, currency) => {
		onChange(value, currency);
	}, [onChange]);

	return (
		<CurrencyInputLabel
			debounceTimeout={ 700 }
			value={ value }
			label={ label }
			onChange={ handleChange }
			{ ...rest }
		/>
	);
}

CurrencyInput.propTypes = {
	label: PropTypes.node,
	input: PropTypes.shape({
		value: PropTypes.any,
		onChange: PropTypes.func.isRequired,
	}),
};

export function ToggleInput({
	label,
	input: {
		value,
		onChange,
	},
	...rest
}){
	const handleChange = React.useCallback((ev, { checked }) => {
		onChange(checked);
	}, [onChange]);

	// Case in module when input value is for Example : ["false"]
	if (value instanceof Array) {
		value = value[0];
	}

	// in module we have value == "false" but Boolean(string) is true
	if (value === "false") {
		value = false;
	}

	return (
		<ToggleLabel 
			checked={ Boolean(value) }
			label={ label } 
			onChange={ handleChange }
			{ ...rest }
		/>
	);
}

ToggleInput.propTypes = {
	label: PropTypes.node,
	input: PropTypes.shape({
		value: PropTypes.any,
		onChange: PropTypes.func.isRequired,
	}),
};

export function CheckboxInput({
	label,
	input: {
		value,
		onChange,
		name
	},
	...rest
}){
	const handleChange = React.useCallback((ev, { checked }) => {
		onChange(checked ? value : '');
	}, [onChange, value]);

	return (
		<CheckboxLabel
			name={ name }
			checked={ Boolean(value) } 
			label={ label } 
			onChange={ handleChange }
			{ ...rest }
		/>
	);
}

CheckboxInput.propTypes = {
	label: PropTypes.node,
	input: PropTypes.shape({
		value: PropTypes.any,
		onChange: PropTypes.func.isRequired,
		name: PropTypes.string.isRequired,
	}),
};

export const FormInput = ({
	input,
	label,
	required,
	meta: { touched, error, submitError },
	...rest
}) => <Form.Field error={ !!((touched && error) || submitError ) } required={ required } >
	{label && <label>{label}</label>}
	<Input required={ required } { ...input } { ...rest } />
	{ ((touched && error) || submitError) ? (
		<Label basic color="red" pointing>
			{error ||  submitError }
		</Label>
	) : null}
</Form.Field>;

FormInput.propTypes = {
	input: PropTypes.any,
	label: PropTypes.node,
	required: PropTypes.bool,
	meta: PropTypes.shape({
		touched: PropTypes.bool,
		error: PropTypes.node,
		submitError: PropTypes.node,
	}),
};

export const FormInputV2 = ({
	input,
	label,
	meta: { touched, error, submitError },
	...rest
}) => <FieldBlock as={ Input } time={ 2000 } label={ label } errors={ !!((touched && error) || submitError ) ? [error || submitError] : false } { ...input } { ...rest } />;


FormInputV2.propTypes = {
	input: PropTypes.any,
	label: PropTypes.node,
	required: PropTypes.bool,
	meta: PropTypes.shape({
		touched: PropTypes.bool,
		error: PropTypes.node,
		submitError: PropTypes.node,
	}),
};

export const SelectInput = ({
	input,
	meta: { error, submitError },
	...rest
}) => <SelectLabel errors={ !!(error || submitError ) ? [error || submitError] : false } { ...input } { ...rest } />;

SelectInput.propTypes = {
	input: PropTypes.any,
	meta: PropTypes.shape({
		error: PropTypes.node,
		submitError: PropTypes.node,
	}),
};

export const TextAreaInput = ({
	input,
	meta: { touched, error, submitError },
	...rest
}) => <TextAreaLabel errors={ !!((touched && error) || submitError ) ? [error || submitError] : false } { ...input } { ...rest } />;

TextAreaInput.propTypes = {
	input: PropTypes.any,
	meta: PropTypes.shape({
		touched: PropTypes.bool,
		error: PropTypes.node,
		submitError: PropTypes.node,
	}),
};
